// Global fetch interceptor.
//
// Attaches the session token (stored in localStorage by AuthContext at login)
// as an `Authorization: Bearer <token>` header on every same-origin /api/*
// request, so the server can identify the caller. Also attaches the active
// campaign slug, derived from the URL, as `X-Campaign-Slug`. Installed once at
// startup by importing this module in main.jsx — this avoids having to touch
// the dozens of individual fetch() call sites scattered across the app.
//
// It also watches for 401 responses and reports them to whoever has
// registered interest via setUnauthorizedHandler() (AuthContext does this).
// This module deliberately does NOT import AuthContext — the dependency runs
// the other way (AuthContext imports this file) so there is no circular
// import. AuthContext just hands this module a callback to invoke.
//
// setUnauthorizedHandler is also called from a couple of Vitest suites
// (there's no jsdom in this repo — see CLAUDE.md's Testing section) via
// AuthContext.jsx -> this module, at plain Node import time, with no
// `window` global at all. So nothing below may touch `window` at module
// scope; the actual interceptor install is gated on `window` existing.
import { campaignSlugFromPath } from './lib/campaignSlug.js';

// Endpoints where a 401 is an expected, ordinary answer — not evidence the
// session lapsed — so they must never trigger the re-auth modal:
//   - POST /api/auth/login itself returns 401 for a wrong password. Treating
//     that as "your session expired" would be nonsense on the login form.
const UNAUTHORIZED_EXEMPT_PATHS = ['/api/auth/login'];

let unauthorizedHandler = null;

// Registered by AuthContext so it can raise the session-expired modal. Not
// used anywhere else — keep it a single slot, not an event bus, until a
// second subscriber actually needs one.
export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler;
}

if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const originalFetch = window.fetch.bind(window);

  const isApiRequest = (url) => {
    if (typeof url !== 'string') return false;
    return url.startsWith('/api/') || url.startsWith(`${window.location.origin}/api/`);
  };

  const pathnameOf = (url) => {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return url;
    }
  };

  window.fetch = (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isApi = isApiRequest(url);

    if (isApi) {
      const token = localStorage.getItem('sessionToken');
      const slug = campaignSlugFromPath(window.location.pathname);

      if (token || slug) {
        const headers = new Headers(
          init.headers || (typeof input !== 'string' ? input.headers : undefined)
        );
        if (token && !headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        if (slug && !headers.has('X-Campaign-Slug')) {
          headers.set('X-Campaign-Slug', slug);
        }
        init = { ...init, headers };
      }
    }

    return originalFetch(input, init).then((response) => {
      if (
        isApi &&
        response.status === 401 &&
        !UNAUTHORIZED_EXEMPT_PATHS.includes(pathnameOf(url))
      ) {
        unauthorizedHandler?.();
      }
      return response;
    });
  };
}
